import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

/**
 * A session that is safe to send a write under, resolved just before the write
 * goes out (#1657).
 *
 * supabase-js sends `session?.access_token ?? anonKey` on every request. When
 * the stored session disappears while React still holds the user (seen on iOS
 * Safari right after the email-confirmation sign-in), every write silently goes
 * out as role `anon` and fails with "permission denied for function …". This
 * guard refuses to send a write in that state, and restores the session when it
 * can do so safely.
 */
export type WriteSessionResult =
  | { ok: true; session: Session; recovered: boolean }
  | { ok: false; reason: SessionLostReason };

export type SessionLostReason = 'signed_out' | 'user_mismatch' | 'unrecoverable';

/** A write that was refused before it was sent. Never reaches the server. */
export class SessionLostError extends Error {
  readonly reason: SessionLostReason;
  constructor(reason: SessionLostReason) {
    super(reason === 'user_mismatch'
      ? 'A different account is signed in on this device.'
      : 'You have been signed out on this device.');
    this.name = 'SessionLostError';
    this.reason = reason;
  }
}

/** Silent (automatic) restores allowed per user per page load. Manual Reconnect taps are not counted. */
export const MAX_SILENT_RECOVERIES = 3;

const silentRecoveries = new Map<string, number>();
const inflight = new Map<string, Promise<WriteSessionResult>>();

/**
 * A sign-out on this browser that other tabs must respect (#1657 review).
 * AuthContext's last-resort sign-out only deletes the stored session and fires
 * no SIGNED_OUT broadcast, and scope:'local' may leave the server session
 * alive, so a second tab could otherwise silently restore its in-memory copy
 * and sign the browser back in. Set on every sign-out, cleared on SIGNED_IN.
 */
const SIGNED_OUT_KEY = 'lm:signed-out-user';

export function markSignedOut(userId: string | null | undefined): void {
  if (!userId) return;
  try { localStorage.setItem(SIGNED_OUT_KEY, userId); } catch { /* storage unavailable */ }
}

export function clearSignedOutMarker(): void {
  try { localStorage.removeItem(SIGNED_OUT_KEY); } catch { /* storage unavailable */ }
}

function signedOutHere(userId: string): boolean {
  try { return localStorage.getItem(SIGNED_OUT_KEY) === userId; } catch { return false; }
}

/**
 * Resolve a usable session for `expectedUserId`.
 *
 * 1. The stored session belongs to that user → use it. With `forceRefresh`
 *    (after the server refused its token with 401, or a manual Reconnect) it is
 *    refreshed with the server first, so a token the device still trusts but
 *    PostgREST rejects (clock skew, rotated signing key) is never re-sent.
 * 2. It belongs to someone else → `user_mismatch`; never recovered, never sent.
 * 3. There is none, the person did not sign out on this browser, and
 *    `fallback` (React's in-memory copy) belongs to that user → restore it with
 *    `setSession`. auth-js re-validates it with the server (`GET /auth/v1/user`,
 *    which refuses a revoked session) or refreshes it when it has expired, then
 *    stores it again. Only used when storage holds NO session, so a stale
 *    in-memory refresh token never replaces a newer pair another tab stored.
 * 4. Otherwise `unrecoverable` (or `signed_out` when there is nothing to restore).
 *
 * Concurrent callers for the same user share one attempt. Silent restores and
 * silent forced refreshes are capped at MAX_SILENT_RECOVERIES per user per page
 * load. auth-js's own refresh backoff is the only retry loop.
 */
export function ensureWriteSession(
  expectedUserId: string,
  fallback: Session | null,
  opts: { manual?: boolean; forceRefresh?: boolean } = {},
): Promise<WriteSessionResult> {
  const running = inflight.get(expectedUserId);
  if (running) return running;
  const manual = opts.manual === true;
  const attempt = resolve(expectedUserId, fallback, manual, manual || opts.forceRefresh === true)
    .finally(() => inflight.delete(expectedUserId));
  inflight.set(expectedUserId, attempt);
  return attempt;
}

/** Spend one silent recovery for the user; false when the cap is reached. */
function spendSilent(userId: string, manual: boolean): boolean {
  if (manual) return true;
  const used = silentRecoveries.get(userId) ?? 0;
  if (used >= MAX_SILENT_RECOVERIES) return false;
  silentRecoveries.set(userId, used + 1);
  return true;
}

async function resolve(expectedUserId: string, fallback: Session | null, manual: boolean, forceRefresh: boolean): Promise<WriteSessionResult> {
  let stored: Session | null = null;
  try {
    const { data } = await supabase.auth.getSession();
    stored = data.session ?? null;
  } catch {
    stored = null;
  }
  if (stored && stored.user?.id !== expectedUserId) return { ok: false, reason: 'user_mismatch' };
  if (stored) {
    if (!forceRefresh) return { ok: true, session: stored, recovered: false };
    if (!spendSilent(expectedUserId, manual)) return { ok: false, reason: 'unrecoverable' };
    try {
      const { data, error } = await supabase.auth.refreshSession(stored);
      if (error || !data.session) return { ok: false, reason: 'unrecoverable' };
      if (data.session.user?.id !== expectedUserId) return { ok: false, reason: 'user_mismatch' };
      return { ok: true, session: data.session, recovered: true };
    } catch {
      return { ok: false, reason: 'unrecoverable' };
    }
  }

  if (!fallback || !fallback.access_token || !fallback.refresh_token) return { ok: false, reason: 'signed_out' };
  if (fallback.user?.id !== expectedUserId) return { ok: false, reason: 'user_mismatch' };
  if (signedOutHere(expectedUserId)) return { ok: false, reason: 'signed_out' };
  if (!spendSilent(expectedUserId, manual)) return { ok: false, reason: 'unrecoverable' };

  try {
    const { data, error } = await supabase.auth.setSession({
      access_token: fallback.access_token,
      refresh_token: fallback.refresh_token,
    });
    if (error || !data.session) return { ok: false, reason: 'unrecoverable' };
    if (data.session.user?.id !== expectedUserId) return { ok: false, reason: 'user_mismatch' };
    return { ok: true, session: data.session, recovered: true };
  } catch {
    return { ok: false, reason: 'unrecoverable' };
  }
}

/**
 * PostgREST answered 401: the request was not run as an authenticated user —
 * role `anon` lacks EXECUTE (SQLSTATE 42501) or the JWT itself was refused
 * (PGRST3xx). In both cases the function body never ran, so re-sending the same
 * write once is safe. (A 42501 raised inside a function body, such as the
 * admin-only check, comes back as 403 for an authenticated caller.)
 */
export function isAuthRejected(status: number | null | undefined): boolean {
  return status === 401;
}

export function resetWriteSessionForTests(): void {
  silentRecoveries.clear();
  inflight.clear();
}
